import { useEffect, useState } from 'react';
import { dataUrlByteSize, MagicRef } from './model';

export type ImageSize = { w: number; h: number };

// Loads an image off-screen purely to read its natural pixel size — used to show
// a reference's dimensions before generating. Returns null until loaded (or if
// there's no URL / it fails). The URL is the one already rendered as a thumbnail,
// so the decode is served from the browser cache.
export function useImageSize(url: string | null): ImageSize | null {
  const [size, setSize] = useState<ImageSize | null>(null);
  useEffect(() => {
    setSize(null);
    if (!url) return;
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive) setSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = url;
    return () => {
      alive = false;
    };
  }, [url]);
  return size;
}

// Byte weight of a reference. Asset refs report the asset's own file size — the
// same number the finder's details panel shows — so nothing has to be fetched.
// Uploads / clipboard pastes / captured frames are measured from their own URL,
// which is always local: a `data:` URL is counted from its base64 length, a
// `blob:` one is read back from the in-memory blob. Null while unknown.
export function useRefBytes(ref: MagicRef | null): number | null {
  const url = ref?.kind === 'upload' ? ref.url : null;
  const [bytes, setBytes] = useState<number | null>(null);

  useEffect(() => {
    setBytes(null);
    if (!url) return;
    const inline = dataUrlByteSize(url);
    if (inline !== null) {
      setBytes(inline);
      return;
    }
    let alive = true;
    // blob: URL — resolves against the in-memory blob, no network involved.
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        if (alive) setBytes(blob.size);
      })
      // The weight is decorative: a revoked / unreadable URL just stays blank.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [url]);

  return ref?.kind === 'asset' ? ref.asset.size : bytes;
}
